import React, { useCallback, useImperativeHandle, useRef, useState } from "react";
import { isEqual, isFunction } from "@util/utils";
import ConvertToDefectDialog from "./ConvertToDefectDialog";
import AdjustStockQtyDialog from "./AdjustStockQtyDialog";
import DefectClaimDialog from "./DefectClaimDialog";
import DefectStockInDialog from "./DefectStockInDialog";
import ProductClaimDialog from "./ProductClaimDialog";
import ProductInventoryDialog from "./ProductInventoryDialog";
import GiftClaimDialog from "./GiftClaimDialog";

const CONVERT_TO_DEFECT_DIALOG = "ConvertToDefectDialog";
const ADJUST_STOCK_QTY_DIALOG = "AdjustStockQtyDialog";
const DEFECT_CLAIM_DIALOG = "DefectClaimDialog";
const DEFECT_STOCK_IN_DIALOG = "DefectStockInDialog";
const PRODUCT_CLAIM_DIALOG = "ProductClaimDialog";
const PRODUCT_INVENTORY_DIALOG = "ProductInventoryDialog";
const GIFT_CLAIM_DIALOG = "GiftClaimDialog";

/**
 * 庫存管理 DialogBox 管理器
 * open 方法簽名：openXxxDialog(targetData, refreshFn)
 * targetData：外層提供的資料（各 dialog 從 sourceData 讀取）
 * refreshFn：供 dialog 情境使用（送出後呼叫）
 */
const StockDialogBox = React.forwardRef((props, ref) => {
	const [openDialog, setOpenDialog] = useState("");
	const [sourceData, setSourceData] = useState(null);
	const refreshRef = useRef(null);
	const syncReturnFlowRef = useRef(null);

	const handleDialogClose = useCallback(() => {
		setOpenDialog("");
		setSourceData(null);
		refreshRef.current = null;
		syncReturnFlowRef.current = null;
	}, []);

	const doRefresh = (...arg) => {
		const fn = refreshRef.current;
		if (isFunction(fn)) {
			setTimeout(() => fn(...arg), 5);
		}
	};

	useImperativeHandle(
		ref,
		() => ({
			// 開啟瑕疵回報彈窗；targetData: { product?, employeeOptions? }
			// onSyncReturnFlow：isSyncReturnFlow=true 且 API 成功時由頁面層決定後續行為（如開啟退貨單）
			openConvertToDefectDialog: (targetData, refreshFn, onSyncReturnFlow) => {
				refreshRef.current = refreshFn;
				syncReturnFlowRef.current = onSyncReturnFlow ?? null;
				setSourceData(targetData ?? null);
				setOpenDialog(CONVERT_TO_DEFECT_DIALOG);
			},
			// 開啟商品庫存數量調整彈窗；targetData: { product?, employeeOptions? }
			openAdjustStockQtyDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData ?? null);
				setOpenDialog(ADJUST_STOCK_QTY_DIALOG);
			},
			// 開啟瑕疵領用彈窗；targetData: { product?, defectList?, employeeOptions? }
			openDefectClaimDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData ?? null);
				setOpenDialog(DEFECT_CLAIM_DIALOG);
			},
			// 開啟瑕疵入庫彈窗；targetData: { productID?, variantID? }
			openDefectStockInDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData ?? null);
				setOpenDialog(DEFECT_STOCK_IN_DIALOG);
			},
			// 開啟商品領用彈窗（建單人選項由 Dialog 內部載入；targetData 保留參數僅為與其他 open 簽名一致，可傳 null）
			openProductClaimDialog: (_targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setOpenDialog(PRODUCT_CLAIM_DIALOG);
			},
			// 開啟商品盤點彈窗；targetData: { product?, employeeOptions? }
			openProductInventoryDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData ?? null);
				setOpenDialog(PRODUCT_INVENTORY_DIALOG);
			},
			// 開啟贈品領用彈窗；targetData: { product?, employeeOptions? }
			openGiftClaimDialog: (targetData, refreshFn) => {
				refreshRef.current = refreshFn;
				setSourceData(targetData ?? null);
				setOpenDialog(GIFT_CLAIM_DIALOG);
			},
			closeDialog: () => handleDialogClose(),
		}),
		[handleDialogClose],
	);

	return (
		<>
			<ConvertToDefectDialog
				open={isEqual(openDialog, CONVERT_TO_DEFECT_DIALOG)}
				onClose={handleDialogClose}
				sourceData={sourceData}
				refresh={doRefresh}
				onSyncReturnFlow={syncReturnFlowRef.current}
			/>
			<AdjustStockQtyDialog open={isEqual(openDialog, ADJUST_STOCK_QTY_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} refresh={doRefresh} />
			<DefectClaimDialog open={isEqual(openDialog, DEFECT_CLAIM_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} refresh={doRefresh} />
			<DefectStockInDialog open={isEqual(openDialog, DEFECT_STOCK_IN_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} refresh={doRefresh} />
			<ProductClaimDialog open={isEqual(openDialog, PRODUCT_CLAIM_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} />
			<ProductInventoryDialog open={isEqual(openDialog, PRODUCT_INVENTORY_DIALOG)} onClose={handleDialogClose} refresh={doRefresh} />
			<GiftClaimDialog open={isEqual(openDialog, GIFT_CLAIM_DIALOG)} onClose={handleDialogClose} sourceData={sourceData} refresh={doRefresh} />
		</>
	);
});

StockDialogBox.displayName = "StockDialogBox";

export default StockDialogBox;
